import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill/dist/quill.snow.css";
import RichTextToolbarControls from "./RichTextToolbarControls";

const FIRST_NAME_TOKEN = "[[PERSON_FIRST_NAME]]";

const FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "color",
  "background",
  "link",
  "list",
];

export const applyEmailEditorFormat = (
  quill,
  savedSelection,
  format,
  value,
) => {
  const range = quill.getSelection() || savedSelection.current;
  if (!range) return {};
  const nextValue = value === undefined || value === null ? false : value;
  if (range.length > 0) {
    quill.formatText(range.index, range.length, format, nextValue, "user");
  } else {
    quill.setSelection(range, "silent");
    quill.format(format, nextValue, "user");
  }
  savedSelection.current = range;
  quill.setSelection(range, "silent");
  quill.focus({ preventScroll: true });
  return quill.getFormat(range.index, range.length);
};

export const toggleEmailEditorFormat = (quill, savedSelection, format) => {
  const range = quill.getSelection() || savedSelection.current;
  if (!range) return {};
  const currentFormats = quill.getFormat(range.index, range.length);
  return applyEmailEditorFormat(
    quill,
    savedSelection,
    format,
    !Boolean(currentFormats[format]),
  );
};

export default function EmailRichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const savedSelection = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});
  const reactId = useId();
  const toolbarId = `email-editor-toolbar-${reactId.replace(/[^a-z0-9_-]/gi, "")}`;
  const modules = useMemo(
    () => ({ toolbar: { container: `#${toolbarId}` } }),
    [toolbarId],
  );

  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return undefined;

    const closePickers = (except = null) => {
      toolbar.querySelectorAll(".ql-picker.ql-expanded").forEach((picker) => {
        if (picker !== except) picker.classList.remove("ql-expanded");
      });
    };
    const handleToolbarClick = (event) => {
      const selectedPicker = event.target.closest(".ql-picker");
      if (!selectedPicker) return;
      // Quill toggles its picker during the same click. Run immediately after
      // that handler and close every competing picker.
      window.setTimeout(() => closePickers(selectedPicker), 0);
    };
    const handleOutsidePointerDown = (event) => {
      if (!toolbar.contains(event.target)) {
        closePickers();
      }
    };

    toolbar.addEventListener("click", handleToolbarClick);
    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => {
      toolbar.removeEventListener("click", handleToolbarClick);
      document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
    };
  }, []);

  const toggleFormat = (format) => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;
    setActiveFormats(
      toggleEmailEditorFormat(editor, savedSelection, format) || {},
    );
  };

  const applyColour = (format, colour) => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;
    setActiveFormats(
      applyEmailEditorFormat(editor, savedSelection, format, colour) || {},
    );
  };

  const insertFirstName = () => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;
    const selection = editor.getSelection(true);
    const index = selection?.index ?? Math.max(0, editor.getLength() - 1);
    editor.insertText(index, FIRST_NAME_TOKEN, "user");
    editor.setSelection(index + FIRST_NAME_TOKEN.length, 0, "silent");
  };

  return (
    <div className="email-rich-text-editor">
      <div ref={toolbarRef} id={toolbarId} className="email-rich-text-toolbar">
        <select className="ql-header" defaultValue="" aria-label="Text style">
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
          <option value="">Normal</option>
        </select>
        <RichTextToolbarControls
          activeFormats={activeFormats}
          onToggle={toggleFormat}
          onApplyColour={applyColour}
        />
        <button type="button" className="ql-link" aria-label="Insert link" title="Insert link" />
        <button type="button" className="ql-list" value="ordered" aria-label="Numbered list" title="Numbered list" />
        <button type="button" className="ql-list" value="bullet" aria-label="Bullet list" title="Bullet list" />
        <button type="button" className="ql-clean" aria-label="Clear formatting" title="Clear formatting" />
        <button
          type="button"
          className="email-insert-first-name"
          onClick={insertFirstName}
          title="Insert the recipient's first name"
        >
          Insert First Name
        </button>
      </div>
      <ReactQuill
        ref={editorRef}
        theme="snow"
        value={value}
        onChange={onChange}
        onChangeSelection={(range) => {
          if (!range) return;
          savedSelection.current = range;
          const editor = editorRef.current?.getEditor();
          if (editor) {
            setActiveFormats(editor.getFormat(range.index, range.length) || {});
          }
        }}
        modules={modules}
        formats={FORMATS}
      />
    </div>
  );
}
